import type {SyncConfigComponentProps} from '@cohort/merchants/apps';
import {useBrevoContactAttributes} from '@cohort/merchants/apps/brevo/actions';
import ExportUsersMappedUserProperties from '@cohort/merchants/apps/brevo/sync/ExportUsersMappedUserProperties';
import {RadioCards} from '@cohort/merchants/components/form/RadioCards';
import HighlightText from '@cohort/merchants/components/HighlightText';
import LoadingState from '@cohort/merchants/components/LoadingState';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import {useCohortForm} from '@cohort/merchants/hooks/contexts/form';
import type {SyncConfigFormValues} from '@cohort/merchants/pages/apps/app/utils';
import {useTranslation} from 'react-i18next';

type ExportUsersConfigProps = SyncConfigComponentProps;
const ExportUsersConfig: React.FC<ExportUsersConfigProps> = ({connection}) => {
  const merchant = useCurrentMerchant();

  const {setValue, register, control, watch} =
    useCohortForm<Extract<SyncConfigFormValues, {appId: 'brevo'}>>();

  const {t} = useTranslation('app-brevo', {
    keyPrefix: 'sync.exportUsersConfig',
  });

  const {data: getContactAttributesResponse, isFetched: isBrevoContactAttributesFetched} =
    useBrevoContactAttributes(merchant.id, connection.id);
  const brevoContactAttributes = getContactAttributesResponse?.output.attributes ?? [];

  const isExportEnabled = watch('userExportEnabled');
  const createBrevoContacts = watch('userExportConfig.createBrevoContacts');

  if (!isBrevoContactAttributesFetched) {
    return <LoadingState className="bg-white" />;
  }

  return (
    <div className="flex flex-col space-y-4">
      <RadioCards
        name="userExportConfig.createBrevoContacts"
        label={t('labelUserGroup')}
        register={register}
        control={control}
        disabled={!isExportEnabled}
        options={[
          {label: t('labelAllUsers'), value: 'true'},
          {label: t('labelExistingBrevoContacts'), value: 'false'},
        ]}
        onChange={e => {
          setValue('userExportConfig.createBrevoContacts', e.target.value === 'true');
        }}
        preventFieldOnChange
      />
      {isExportEnabled && createBrevoContacts === false && (
        <HighlightText text={t('highlightTextUserExportWarning')} type="warning" />
      )}
      <ExportUsersMappedUserProperties brevoContactAttributes={brevoContactAttributes} />
    </div>
  );
};

export default ExportUsersConfig;
